<script setup>
import { computed, ref } from 'vue';
import { format, parseISO } from 'date-fns';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';

import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  orders: {
    type: Array,
    default: () => [],
  },
  productsOrdersFeatures: {
    type: Object,
    default: () => ({}),
  },
  showEditAction: {
    type: Boolean,
    default: false,
  },
  showStatusActions: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(['edit', 'advance', 'cancel', 'complete']);

const STANDARD_ORDER_FLOW = ['pending', 'in_progress', 'completed'];
const ADVANCED_ORDER_FLOW = [
  'pending',
  'confirmed',
  'preparing',
  'shipped',
  'delivered',
];

const { t } = useI18n();
const route = useRoute();
const router = useRouter();

const dialogRef = ref(null);
const selectedOrderId = ref(null);

const currentOrder = computed(
  () => props.orders.find(order => order.id === selectedOrderId.value) || null
);

const orderFlowStatuses = computed(() =>
  props.productsOrdersFeatures?.advancedStatuses
    ? ADVANCED_ORDER_FLOW
    : STANDARD_ORDER_FLOW
);

const formatDateTime = value => {
  if (!value) return t('PRODUCTS_ORDERS.LABELS.NOT_AVAILABLE');

  return format(parseISO(value), 'MMM d, yyyy HH:mm');
};

const statusLabel = status =>
  t(`PRODUCTS_ORDERS.STATUS.${status.toUpperCase()}`);

const paymentStatusLabel = status =>
  t(`PRODUCTS_ORDERS.PAYMENTS.STATUS.${String(status || 'pending').toUpperCase()}`);

const actionTypeLabel = actionType =>
  t(`PRODUCTS_ORDERS.ACTIONS.TYPE_${actionType.toUpperCase()}`);

const statusBadgeClass = status => {
  const classes = {
    pending:
      'border-n-amber-6/40 bg-n-amber-3 text-n-amber-11 dark:border-n-amber-9/40 dark:bg-n-amber-3/10 dark:text-n-amber-11',
    confirmed:
      'border-n-blue-6/40 bg-n-blue-3 text-n-blue-11 dark:border-n-blue-9/40 dark:bg-n-blue-3/10 dark:text-n-blue-11',
    preparing:
      'border-n-indigo-6/40 bg-n-indigo-3 text-n-indigo-11 dark:border-n-indigo-9/40 dark:bg-n-indigo-3/10 dark:text-n-indigo-11',
    shipped:
      'border-n-cyan-6/40 bg-n-cyan-3 text-n-cyan-11 dark:border-n-cyan-9/40 dark:bg-n-cyan-3/10 dark:text-n-cyan-11',
    delivered:
      'border-n-teal-6/40 bg-n-teal-3 text-n-teal-11 dark:border-n-teal-9/40 dark:bg-n-teal-3/10 dark:text-n-teal-11',
    in_progress:
      'border-n-blue-6/40 bg-n-brand/10 text-n-blue-11 dark:border-n-blue-9/40 dark:bg-n-brand/10 dark:text-n-blue-11',
    completed:
      'border-n-teal-6/40 bg-n-teal-3 text-n-teal-11 dark:border-n-teal-9/40 dark:bg-n-teal-3/10 dark:text-n-teal-11',
    canceled:
      'border-n-ruby-6/40 bg-n-ruby-3 text-n-ruby-11 dark:border-n-ruby-9/40 dark:bg-n-ruby-3/10 dark:text-n-ruby-11',
    failed:
      'border-n-ruby-6/40 bg-n-ruby-3 text-n-ruby-11 dark:border-n-ruby-9/40 dark:bg-n-ruby-3/10 dark:text-n-ruby-11',
    skipped:
      'border-n-slate-6/40 bg-n-alpha-2 text-n-slate-11 dark:border-n-slate-7/40 dark:bg-n-alpha-2 dark:text-n-slate-11',
  };

  return classes[status] || classes.pending;
};

const paymentStatusBadgeClass = status => {
  const classes = {
    pending:
      'border-n-amber-6/40 bg-n-amber-3 text-n-amber-11 dark:border-n-amber-9/40 dark:bg-n-amber-3/10 dark:text-n-amber-11',
    partially_paid:
      'border-n-blue-6/40 bg-n-blue-3 text-n-blue-11 dark:border-n-blue-9/40 dark:bg-n-blue-3/10 dark:text-n-blue-11',
    paid:
      'border-n-teal-6/40 bg-n-teal-3 text-n-teal-11 dark:border-n-teal-9/40 dark:bg-n-teal-3/10 dark:text-n-teal-11',
    refunded:
      'border-n-indigo-6/40 bg-n-indigo-3 text-n-indigo-11 dark:border-n-indigo-9/40 dark:bg-n-indigo-3/10 dark:text-n-indigo-11',
    failed:
      'border-n-ruby-6/40 bg-n-ruby-3 text-n-ruby-11 dark:border-n-ruby-9/40 dark:bg-n-ruby-3/10 dark:text-n-ruby-11',
  };

  return classes[status] || classes.pending;
};

const formatCurrencyAmount = (amount, currency = 'USD') => {
  const normalizedAmount = Number(amount || 0);

  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      maximumFractionDigits: 2,
    }).format(normalizedAmount);
  } catch (error) {
    return `${currency} ${normalizedAmount.toFixed(2)}`;
  }
};

const normalizeStatusForActiveFlow = status => {
  if (props.productsOrdersFeatures?.advancedStatuses) {
    if (status === 'in_progress') return 'preparing';
    if (status === 'completed') return 'delivered';
    return status;
  }

  if (['confirmed', 'preparing', 'shipped'].includes(status)) {
    return 'in_progress';
  }
  if (status === 'delivered') {
    return 'completed';
  }

  return status;
};

const nextStatusForOrder = order => {
  if (!order) return null;

  const normalizedStatus = normalizeStatusForActiveFlow(order.status);
  const currentIndex = orderFlowStatuses.value.indexOf(normalizedStatus);
  if (currentIndex === -1) {
    return null;
  }

  return orderFlowStatuses.value[currentIndex + 1] || null;
};

const isTerminalOrderStatus = order => {
  if (!order) return false;

  const normalizedStatus = normalizeStatusForActiveFlow(order.status);
  return ['canceled', orderFlowStatuses.value.at(-1)].includes(normalizedStatus);
};

const canCancelOrder = order => {
  if (!order) return false;

  const normalizedStatus = normalizeStatusForActiveFlow(order.status);
  const terminalStatus = orderFlowStatuses.value.at(-1);
  return !['canceled', terminalStatus].includes(normalizedStatus);
};

const timelineDescription = event => {
  const actor = event.actor?.name || t('PRODUCTS_ORDERS.LABELS.SYSTEM');

  if (event.eventType === 'created') {
    return t('PRODUCTS_ORDERS.TIMELINE.CREATED', { actor });
  }

  if (event.eventType === 'status_changed') {
    return t('PRODUCTS_ORDERS.TIMELINE.STATUS_CHANGED', {
      actor,
      from: statusLabel(event.statusFrom),
      to: statusLabel(event.statusTo),
    });
  }

  if (event.eventType === 'payment_status_changed') {
    return t('PRODUCTS_ORDERS.TIMELINE.PAYMENT_STATUS_CHANGED', {
      actor,
      from: paymentStatusLabel(event.statusFrom),
      to: paymentStatusLabel(event.statusTo),
    });
  }

  if (event.eventType === 'stuck_alerted') {
    return t('PRODUCTS_ORDERS.TIMELINE.STUCK_ALERTED', {
      status: statusLabel(event.metadata?.status || 'pending'),
      hours: event.metadata?.thresholdHours || event.metadata?.threshold_hours || 0,
    });
  }

  if (event.eventType === 'webhook_dispatched') {
    return t('PRODUCTS_ORDERS.TIMELINE.WEBHOOK_DISPATCHED', {
      event: event.metadata?.eventName || event.metadata?.event_name || t('PRODUCTS_ORDERS.LABELS.NOT_AVAILABLE'),
      count: event.metadata?.webhookCount || event.metadata?.webhook_count || 0,
    });
  }

  return t('PRODUCTS_ORDERS.TIMELINE.ACTION_EXECUTED', {
    actor,
    action: event.metadata?.actionName || t('PRODUCTS_ORDERS.LABELS.NOT_AVAILABLE'),
  });
};

const productDisplayName = order => {
  if (order?.productVariant?.name && order?.product?.name) {
    return `${order.product.name} · ${order.productVariant.name}`;
  }

  return (
    order?.productVariant?.name ||
    order?.product?.name ||
    t('PRODUCTS_ORDERS.LABELS.PRODUCT_NOT_SELECTED')
  );
};

const open = order => {
  if (!order?.id) {
    return;
  }

  selectedOrderId.value = order.id;
  dialogRef.value?.open?.();
};

const close = () => {
  dialogRef.value?.close?.();
};

const openConversation = () => {
  if (!currentOrder.value?.conversationId) {
    return;
  }

  router.push({
    name: 'inbox_conversation',
    params: {
      accountId: route.params.accountId,
      conversation_id: currentOrder.value.conversationId,
    },
  });
};

const handleEdit = () => {
  if (!currentOrder.value) {
    return;
  }

  emit('edit', currentOrder.value);
  close();
};

const handleAdvance = () => {
  if (currentOrder.value) {
    emit('advance', currentOrder.value);
  }
};

const handleComplete = () => {
  if (currentOrder.value) {
    emit('complete', currentOrder.value);
  }
};

const handleCancel = () => {
  if (currentOrder.value) {
    emit('cancel', currentOrder.value);
  }
};

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="3xl"
    :title="
      currentOrder
        ? `${$t('PRODUCTS_ORDERS.DETAILS.TITLE')} · ${currentOrder.reference}`
        : $t('PRODUCTS_ORDERS.DETAILS.TITLE')
    "
    :description="$t('PRODUCTS_ORDERS.DETAILS.DESCRIPTION')"
    :show-confirm-button="false"
    :show-cancel-button="false"
    overflow-y-auto
  >
    <div
      v-if="currentOrder"
      class="flex max-h-[75vh] flex-col gap-6 overflow-y-auto pr-1"
    >
      <section class="grid gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(20rem,1fr)]">
        <div class="rounded-xl border border-n-weak bg-n-surface-1 p-4">
          <div class="flex flex-wrap items-center gap-2">
            <h3 class="mb-0 text-lg font-semibold text-n-slate-12">
              {{ currentOrder.reference }}
            </h3>
            <span
              class="inline-flex rounded-full border px-2 py-1 text-xs font-semibold"
              :class="statusBadgeClass(currentOrder.status)"
            >
              {{ statusLabel(currentOrder.status) }}
            </span>
            <span
              class="inline-flex rounded-full border px-2 py-1 text-xs font-semibold"
              :class="paymentStatusBadgeClass(currentOrder.paymentStatus)"
            >
              {{ paymentStatusLabel(currentOrder.paymentStatus) }}
            </span>
          </div>
          <p class="mt-2 mb-0 text-sm font-medium text-n-slate-12">
            {{ productDisplayName(currentOrder) }}
          </p>
          <div class="mt-4 grid gap-3 md:grid-cols-2">
            <div class="rounded-lg bg-n-alpha-2 p-3">
              <p class="mb-1 text-xs uppercase tracking-[0.08em] text-n-slate-10">
                {{ $t('PRODUCTS_ORDERS.LABELS.CREATED_AT') }}
              </p>
              <p class="mb-0 text-sm text-n-slate-12">
                {{ formatDateTime(currentOrder.createdAt) }}
              </p>
            </div>
            <div class="rounded-lg bg-n-alpha-2 p-3">
              <p class="mb-1 text-xs uppercase tracking-[0.08em] text-n-slate-10">
                {{ $t('PRODUCTS_ORDERS.FIELDS.SOURCE') }}
              </p>
              <p class="mb-0 text-sm text-n-slate-12">
                {{ currentOrder.source }}
              </p>
            </div>
            <div class="rounded-lg bg-n-alpha-2 p-3">
              <p class="mb-1 text-xs uppercase tracking-[0.08em] text-n-slate-10">
                {{ $t('PRODUCTS_ORDERS.FIELDS.QUANTITY') }}
              </p>
              <p class="mb-0 text-sm text-n-slate-12">
                {{
                  $t('PRODUCTS_ORDERS.LABELS.QUANTITY', {
                    count: currentOrder.quantity,
                  })
                }}
              </p>
            </div>
            <div class="rounded-lg bg-n-alpha-2 p-3">
              <p class="mb-1 text-xs uppercase tracking-[0.08em] text-n-slate-10">
                {{ $t('PRODUCTS_ORDERS.LABELS.CONVERSATION') }}
              </p>
              <p class="mb-0 text-sm text-n-slate-12">
                {{
                  currentOrder.conversationId ||
                  $t('PRODUCTS_ORDERS.LABELS.NOT_AVAILABLE')
                }}
              </p>
            </div>
          </div>
        </div>

        <div class="rounded-xl border border-n-weak bg-n-surface-1 p-4">
          <div class="flex items-center gap-2">
            <Icon icon="i-lucide-user-round" class="size-4 text-n-brand" />
            <h4 class="mb-0 text-sm font-semibold text-n-slate-12">
              {{ $t('PRODUCTS_ORDERS.DETAILS.CONTACT_TITLE') }}
            </h4>
          </div>
          <div class="mt-4 space-y-3 text-sm text-n-slate-11">
            <div>
              <p class="mb-1 text-xs uppercase tracking-[0.08em] text-n-slate-10">
                {{ $t('PRODUCTS_ORDERS.FIELDS.CONTACT_NAME') }}
              </p>
              <p class="mb-0 text-n-slate-12">
                {{ currentOrder.contactName }}
              </p>
            </div>
            <div>
              <p class="mb-1 text-xs uppercase tracking-[0.08em] text-n-slate-10">
                {{ $t('PRODUCTS_ORDERS.FIELDS.CONTACT_PHONE') }}
              </p>
              <p class="mb-0 text-n-slate-12">
                {{
                  currentOrder.contactPhone ||
                  $t('PRODUCTS_ORDERS.LABELS.NOT_AVAILABLE')
                }}
              </p>
            </div>
            <div>
              <p class="mb-1 text-xs uppercase tracking-[0.08em] text-n-slate-10">
                {{ $t('PRODUCTS_ORDERS.FIELDS.CONTACT_EMAIL') }}
              </p>
              <p class="mb-0 text-n-slate-12">
                {{
                  currentOrder.contactEmail ||
                  $t('PRODUCTS_ORDERS.LABELS.NOT_AVAILABLE')
                }}
              </p>
            </div>
          </div>
        </div>
      </section>

      <section class="grid gap-4 lg:grid-cols-2">
        <div class="rounded-xl border border-n-weak bg-n-surface-1 p-4">
          <div class="flex items-center gap-2">
            <Icon icon="i-lucide-map-pinned" class="size-4 text-n-brand" />
            <h4 class="mb-0 text-sm font-semibold text-n-slate-12">
              {{ $t('PRODUCTS_ORDERS.DETAILS.DELIVERY_TITLE') }}
            </h4>
          </div>
          <div class="mt-4 space-y-3 text-sm text-n-slate-11">
            <div>
              <p class="mb-1 text-xs uppercase tracking-[0.08em] text-n-slate-10">
                {{ $t('PRODUCTS_ORDERS.FIELDS.ADDRESS') }}
              </p>
              <p class="mb-0 text-n-slate-12">
                {{
                  currentOrder.address ||
                  $t('PRODUCTS_ORDERS.LABELS.NOT_AVAILABLE')
                }}
              </p>
            </div>
            <div>
              <p class="mb-1 text-xs uppercase tracking-[0.08em] text-n-slate-10">
                {{ $t('PRODUCTS_ORDERS.FIELDS.LOCATION') }}
              </p>
              <p class="mb-0 text-n-slate-12">
                {{
                  currentOrder.location ||
                  $t('PRODUCTS_ORDERS.LABELS.NOT_AVAILABLE')
                }}
              </p>
            </div>
          </div>
        </div>

        <div class="rounded-xl border border-n-weak bg-n-surface-1 p-4">
          <div class="flex items-center gap-2">
            <Icon icon="i-lucide-notebook-pen" class="size-4 text-n-brand" />
            <h4 class="mb-0 text-sm font-semibold text-n-slate-12">
              {{ $t('PRODUCTS_ORDERS.FIELDS.NOTES') }}
            </h4>
          </div>
          <p class="mt-4 mb-0 whitespace-pre-wrap text-sm text-n-slate-11">
            {{ currentOrder.notes || $t('PRODUCTS_ORDERS.LABELS.NO_NOTES') }}
          </p>
        </div>
      </section>

      <section class="grid gap-4 lg:grid-cols-3">
        <div class="rounded-xl border border-n-weak bg-n-surface-1 p-4">
          <p class="mb-1 text-xs uppercase tracking-[0.08em] text-n-slate-10">
            {{ $t('PRODUCTS_ORDERS.FIELDS.TRACKING_NUMBER') }}
          </p>
          <p class="mb-0 text-sm text-n-slate-12">
            {{
              currentOrder.trackingNumber ||
              $t('PRODUCTS_ORDERS.LABELS.NOT_AVAILABLE')
            }}
          </p>
        </div>
        <div class="rounded-xl border border-n-weak bg-n-surface-1 p-4">
          <p class="mb-1 text-xs uppercase tracking-[0.08em] text-n-slate-10">
            {{ $t('PRODUCTS_ORDERS.FIELDS.PAYMENT_METHOD') }}
          </p>
          <p class="mb-0 text-sm text-n-slate-12">
            {{
              currentOrder.paymentMethod ||
              $t('PRODUCTS_ORDERS.LABELS.NOT_AVAILABLE')
            }}
          </p>
        </div>
        <div class="rounded-xl border border-n-weak bg-n-surface-1 p-4">
          <p class="mb-1 text-xs uppercase tracking-[0.08em] text-n-slate-10">
            {{ $t('PRODUCTS_ORDERS.FIELDS.RESPONSIBLE_AGENT') }}
          </p>
          <p class="mb-0 text-sm text-n-slate-12">
            {{
              currentOrder.responsibleAgent?.name ||
              $t('PRODUCTS_ORDERS.LABELS.NOT_AVAILABLE')
            }}
          </p>
        </div>
      </section>

      <section class="rounded-xl border border-n-weak bg-n-surface-1 p-4">
        <div class="flex items-center gap-2">
          <Icon icon="i-lucide-wallet-cards" class="size-4 text-n-brand" />
          <h4 class="mb-0 text-sm font-semibold text-n-slate-12">
            {{ $t('PRODUCTS_ORDERS.PAYMENTS.TITLE') }}
          </h4>
        </div>
        <div class="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
          <div class="rounded-lg bg-n-alpha-2 p-3">
            <p class="mb-1 text-xs uppercase tracking-[0.08em] text-n-slate-10">
              {{ $t('PRODUCTS_ORDERS.FIELDS.SUBTOTAL_AMOUNT') }}
            </p>
            <p class="mb-0 text-sm text-n-slate-12">
              {{
                formatCurrencyAmount(
                  currentOrder.subtotalAmount,
                  currentOrder.product?.currency
                )
              }}
            </p>
          </div>
          <div class="rounded-lg bg-n-alpha-2 p-3">
            <p class="mb-1 text-xs uppercase tracking-[0.08em] text-n-slate-10">
              {{ $t('PRODUCTS_ORDERS.FIELDS.DISCOUNT_AMOUNT') }}
            </p>
            <p class="mb-0 text-sm text-n-slate-12">
              {{
                formatCurrencyAmount(
                  currentOrder.discountAmount,
                  currentOrder.product?.currency
                )
              }}
            </p>
          </div>
          <div class="rounded-lg bg-n-alpha-2 p-3">
            <p class="mb-1 text-xs uppercase tracking-[0.08em] text-n-slate-10">
              {{ $t('PRODUCTS_ORDERS.FIELDS.SHIPPING_AMOUNT') }}
            </p>
            <p class="mb-0 text-sm text-n-slate-12">
              {{
                formatCurrencyAmount(
                  currentOrder.shippingAmount,
                  currentOrder.product?.currency
                )
              }}
            </p>
          </div>
          <div class="rounded-lg bg-n-alpha-2 p-3">
            <p class="mb-1 text-xs uppercase tracking-[0.08em] text-n-slate-10">
              {{ $t('PRODUCTS_ORDERS.FIELDS.TOTAL_AMOUNT') }}
            </p>
            <p class="mb-0 text-sm text-n-slate-12">
              {{
                formatCurrencyAmount(
                  currentOrder.totalAmount,
                  currentOrder.product?.currency
                )
              }}
            </p>
          </div>
          <div class="rounded-lg bg-n-alpha-2 p-3">
            <p class="mb-1 text-xs uppercase tracking-[0.08em] text-n-slate-10">
              {{ $t('PRODUCTS_ORDERS.FIELDS.PAID_AMOUNT') }}
            </p>
            <p class="mb-0 text-sm text-n-slate-12">
              {{
                formatCurrencyAmount(
                  currentOrder.paidAmount,
                  currentOrder.product?.currency
                )
              }}
            </p>
          </div>
          <div class="rounded-lg bg-n-alpha-2 p-3">
            <p class="mb-1 text-xs uppercase tracking-[0.08em] text-n-slate-10">
              {{ $t('PRODUCTS_ORDERS.FIELDS.OUTSTANDING_AMOUNT') }}
            </p>
            <p class="mb-0 text-sm text-n-slate-12">
              {{
                formatCurrencyAmount(
                  currentOrder.outstandingAmount,
                  currentOrder.product?.currency
                )
              }}
            </p>
          </div>
          <div class="rounded-lg bg-n-alpha-2 p-3">
            <p class="mb-1 text-xs uppercase tracking-[0.08em] text-n-slate-10">
              {{ $t('PRODUCTS_ORDERS.FIELDS.PAYMENT_METHOD') }}
            </p>
            <p class="mb-0 text-sm text-n-slate-12">
              {{
                currentOrder.paymentMethod ||
                $t('PRODUCTS_ORDERS.LABELS.NOT_AVAILABLE')
              }}
            </p>
          </div>
          <div class="rounded-lg bg-n-alpha-2 p-3">
            <p class="mb-1 text-xs uppercase tracking-[0.08em] text-n-slate-10">
              {{ $t('PRODUCTS_ORDERS.FIELDS.PAYMENT_PROOF_URL') }}
            </p>
            <a
              v-if="currentOrder.paymentProofUrl"
              :href="currentOrder.paymentProofUrl"
              target="_blank"
              rel="noreferrer"
              class="text-sm text-n-brand underline"
            >
              {{ $t('PRODUCTS_ORDERS.PAYMENTS.OPEN_PROOF') }}
            </a>
            <p
              v-else
              class="mb-0 text-sm text-n-slate-12"
            >
              {{ $t('PRODUCTS_ORDERS.LABELS.NOT_AVAILABLE') }}
            </p>
          </div>
        </div>
      </section>

      <section class="rounded-xl border border-n-weak bg-n-surface-1 p-4">
        <div class="flex items-center gap-2">
          <Icon icon="i-lucide-clipboard-list" class="size-4 text-n-brand" />
          <h4 class="mb-0 text-sm font-semibold text-n-slate-12">
            {{ $t('PRODUCTS_ORDERS.FIELDS.FULFILLMENT_NOTES') }}
          </h4>
        </div>
        <p class="mt-4 mb-0 whitespace-pre-wrap text-sm text-n-slate-11">
          {{
            currentOrder.fulfillmentNotes ||
            $t('PRODUCTS_ORDERS.LABELS.NO_NOTES')
          }}
        </p>
      </section>

      <section class="rounded-xl border border-n-weak bg-n-surface-1 p-4">
        <div class="mb-4 flex items-center justify-between gap-2">
          <div class="flex items-center gap-2">
            <Icon icon="i-lucide-list-ordered" class="size-4 text-n-brand" />
            <h4 class="mb-0 text-sm font-semibold text-n-slate-12">
              {{ $t('PRODUCTS_ORDERS.DETAILS.ACTIONS_TITLE') }}
            </h4>
          </div>
          <span class="text-xs text-n-slate-10">
            {{ currentOrder.productOrderActions?.length || 0 }}
          </span>
        </div>

        <div
          v-if="!currentOrder.productOrderActions?.length"
          class="rounded-lg border border-dashed border-n-weak px-3 py-6 text-center text-sm text-n-slate-10"
        >
          {{ $t('PRODUCTS_ORDERS.ORDERS.NO_ACTIONS') }}
        </div>

        <div v-else class="flex flex-col gap-3">
          <div
            v-for="(action, index) in currentOrder.productOrderActions"
            :key="action.id"
            class="rounded-lg border border-n-weak bg-n-alpha-1 p-3"
          >
            <div class="flex flex-wrap items-center gap-2">
              <span
                class="text-xs font-semibold uppercase tracking-[0.08em] text-n-slate-10"
              >
                {{
                  $t('PRODUCTS_ORDERS.LABELS.POSITION', {
                    count: index + 1,
                  })
                }}
              </span>
              <span
                class="inline-flex rounded-full border px-2 py-1 text-xs font-semibold"
                :class="statusBadgeClass(action.status)"
              >
                {{ statusLabel(action.status) }}
              </span>
            </div>
            <p class="mt-2 mb-1 text-sm font-semibold text-n-slate-12">
              {{ action.name }}
            </p>
            <p class="mb-1 text-sm text-n-slate-11">
              {{ actionTypeLabel(action.actionType) }}
            </p>
            <p class="mb-1 text-sm text-n-slate-11">
              {{
                action.triggerStatus === 'manual'
                  ? $t('PRODUCTS_ORDERS.ACTIONS.TRIGGER_MANUAL')
                  : $t('PRODUCTS_ORDERS.ACTIONS.TRIGGER_ON_STATUS', {
                      status: statusLabel(action.triggerStatus),
                    })
              }}
            </p>
            <p class="mb-0 text-sm text-n-slate-11">
              {{
                action.content ||
                action.templatePayload?.name ||
                $t('PRODUCTS_ORDERS.LABELS.NOT_AVAILABLE')
              }}
            </p>
          </div>
        </div>
      </section>

      <section
        v-if="productsOrdersFeatures.timeline"
        class="rounded-xl border border-n-weak bg-n-surface-1 p-4"
      >
        <div class="mb-4 flex items-center justify-between gap-2">
          <div class="flex items-center gap-2">
            <Icon icon="i-lucide-history" class="size-4 text-n-brand" />
            <h4 class="mb-0 text-sm font-semibold text-n-slate-12">
              {{ $t('PRODUCTS_ORDERS.TIMELINE.TITLE') }}
            </h4>
          </div>
          <span class="text-xs text-n-slate-10">
            {{ currentOrder.productOrderEvents?.length || 0 }}
          </span>
        </div>

        <div
          v-if="!currentOrder.productOrderEvents?.length"
          class="rounded-lg border border-dashed border-n-weak px-3 py-6 text-center text-sm text-n-slate-10"
        >
          {{ $t('PRODUCTS_ORDERS.TIMELINE.EMPTY') }}
        </div>

        <div v-else class="flex flex-col gap-3">
          <div
            v-for="event in currentOrder.productOrderEvents"
            :key="event.id"
            class="rounded-lg border border-n-weak bg-n-alpha-1 p-3"
          >
            <p class="mb-1 text-sm font-medium text-n-slate-12">
              {{ timelineDescription(event) }}
            </p>
            <p class="mb-0 text-xs text-n-slate-10">
              {{ formatDateTime(event.occurredAt) }}
            </p>
          </div>
        </div>
      </section>
    </div>

    <template #footer>
      <div class="flex flex-wrap items-center justify-between gap-3">
        <Button
          variant="faded"
          color="slate"
          type="button"
          :label="$t('DIALOG.BUTTONS.CLOSE')"
          @click="close"
        />
        <div class="flex flex-wrap justify-end gap-2">
          <Button
            v-if="
              productsOrdersFeatures.openConversationLink &&
              currentOrder?.conversationId
            "
            variant="ghost"
            color="slate"
            icon="i-lucide-messages-square"
            type="button"
            :label="$t('PRODUCTS_ORDERS.ORDERS.OPEN_CONVERSATION')"
            @click="openConversation"
          />
          <Button
            v-if="showStatusActions && nextStatusForOrder(currentOrder)"
            variant="faded"
            color="blue"
            icon="i-lucide-forward"
            type="button"
            :label="
              $t('PRODUCTS_ORDERS.ORDERS.ADVANCE_TO', {
                status: statusLabel(nextStatusForOrder(currentOrder)),
              })
            "
            @click="handleAdvance"
          />
          <Button
            v-if="
              showStatusActions &&
              !productsOrdersFeatures.advancedStatuses &&
              !isTerminalOrderStatus(currentOrder)
            "
            variant="faded"
            color="teal"
            icon="i-lucide-check-check"
            type="button"
            :label="$t('PRODUCTS_ORDERS.ORDERS.COMPLETE')"
            @click="handleComplete"
          />
          <Button
            v-if="showStatusActions && canCancelOrder(currentOrder)"
            variant="faded"
            color="ruby"
            icon="i-lucide-ban"
            type="button"
            :label="$t('PRODUCTS_ORDERS.ORDERS.CANCEL')"
            @click="handleCancel"
          />
          <Button
            v-if="showEditAction"
            variant="solid"
            color="blue"
            icon="i-lucide-pencil"
            type="button"
            :label="$t('PRODUCTS_ORDERS.ACTIONS.EDIT')"
            @click="handleEdit"
          />
        </div>
      </div>
    </template>
  </Dialog>
</template>
